import {
  type BulkPackSubmissionActionBody,
  type BulkPackSubmissionActionResponse,
  bulkPackSubmissionActionResponseDto,
  type PackScanStatus,
  type PackSubmission,
  type PackSubmissionDetail,
  type PackSubmissionStatus,
  packSubmissionDetailDto,
  packSubmissionDto,
  packSubmissionsPageDto,
  type RescanResponse,
  rescanResponseDto,
  type VersionDiffDto,
  versionDiffDto,
} from '@planuze/admin-contracts';
import { z } from 'zod';
import { ApiError, parseJson, readErrorCode, request } from './http.js';

/* Cliente REST da fila de moderação de packs (TASK-29-14). Consome o admin-service
   (TASK-29-05) — list + approve/reject/request-changes. Sem cross-DB, só HTTP. */

const HTTP_NOT_FOUND = 404;
const HTTP_CONFLICT = 409;

export interface PackSubmissionsQuery {
  readonly status?: PackSubmissionStatus | undefined;
  /* Filtros server-side (ADR-0712): particionam a fila inteira, não só a página keyset carregada. */
  readonly publisherId?: string | undefined;
  readonly scanStatus?: PackScanStatus | undefined;
  /** ISO 8601 — limite inferior de `submittedAt` (janela do período selecionado). */
  readonly submittedAfter?: string | undefined;
  readonly cursor?: string | undefined;
}

async function submissionError(res: Response): Promise<ApiError> {
  const code = await readErrorCode(res);
  switch (res.status) {
    case HTTP_NOT_FOUND:
      return new ApiError(res.status, 'not_found', 'submission not found');
    case HTTP_CONFLICT:
      return new ApiError(res.status, 'invalid_transition', code || 'invalid transition');
    default:
      return new ApiError(res.status, 'server_error', code || 'request failed');
  }
}

function buildQuery(query: PackSubmissionsQuery): string {
  const params = new URLSearchParams();
  if (query.status !== undefined) params.set('status', query.status);
  if (query.publisherId !== undefined && query.publisherId.length > 0) {
    params.set('publisherId', query.publisherId);
  }
  if (query.scanStatus !== undefined) params.set('scanStatus', query.scanStatus);
  if (query.submittedAfter !== undefined) params.set('submittedAfter', query.submittedAfter);
  if (query.cursor !== undefined && query.cursor.length > 0) params.set('cursor', query.cursor);
  const qs = params.toString();
  return qs.length > 0 ? `?${qs}` : '';
}

/** GET /v1/admin/pack-submissions — fila paginada por cursor (keyset submittedAt). */
export async function listPackSubmissions(query: PackSubmissionsQuery): Promise<{
  readonly submissions: readonly PackSubmission[];
  readonly nextCursor: string | null;
}> {
  const res = await request(`/v1/admin/pack-submissions${buildQuery(query)}`);
  if (!res.ok) throw await submissionError(res);
  const page = await parseJson(res, packSubmissionsPageDto);
  return { submissions: page.submissions, nextCursor: page.nextCursor ?? null };
}

/** GET /v1/admin/pack-submissions/:id — detalhe rico (findings/timeline/versões/publisher). */
export async function getPackSubmission(id: string): Promise<PackSubmissionDetail> {
  const res = await request(`/v1/admin/pack-submissions/${encodeURIComponent(id)}`);
  if (!res.ok) throw await submissionError(res);
  return parseJson(res, packSubmissionDetailDto);
}

/** GET .../:id/versions/:a/compare/:b — diff de manifest entre 2 versões publicadas (ADR-0473). */
export async function comparePackVersions(
  id: string,
  a: string,
  b: string,
): Promise<VersionDiffDto> {
  const enc = encodeURIComponent;
  const res = await request(
    `/v1/admin/pack-submissions/${enc(id)}/versions/${enc(a)}/compare/${enc(b)}`,
  );
  if (!res.ok) throw await submissionError(res);
  return parseJson(res, versionDiffDto);
}

const transitionResponse = z.object({ submission: packSubmissionDto });

async function transition(id: string, action: string, body: object): Promise<PackSubmission> {
  const res = await request(`/v1/admin/pack-submissions/${encodeURIComponent(id)}/${action}`, {
    method: 'POST',
    body,
  });
  if (!res.ok) throw await submissionError(res);
  return (await parseJson(res, transitionResponse)).submission;
}

/** POST .../start-review — inicia revisão manual (submitted/scanning → reviewing). Sem motivo. */
export function startReviewPackSubmission(id: string): Promise<PackSubmission> {
  return transition(id, 'start-review', {});
}
/** POST .../reopen — reabre uma rejeição (rejected → reviewing, ADR-0459). Sem motivo
    (preserva o reviewNotes da rejeição). */
export function reopenPackSubmission(id: string): Promise<PackSubmission> {
  return transition(id, 'reopen', {});
}
/** POST .../approve — motivo opcional; promove o pack via outbox (admin-service). */
export function approvePackSubmission(id: string, reason?: string): Promise<PackSubmission> {
  return transition(id, 'approve', reason !== undefined ? { reason } : {});
}
/** POST .../reject — motivo obrigatório. */
export function rejectPackSubmission(id: string, reason: string): Promise<PackSubmission> {
  return transition(id, 'reject', { reason });
}
/** POST .../request-changes — motivo obrigatório; devolve ao publisher para ajuste. */
export function requestChangesPackSubmission(id: string, reason: string): Promise<PackSubmission> {
  return transition(id, 'request-changes', { reason });
}

/** POST .../bulk — mesma transição e justificativa para IDs congelados; parcial por item. */
export async function bulkPackSubmissionAction(
  body: BulkPackSubmissionActionBody,
): Promise<BulkPackSubmissionActionResponse> {
  const res = await request('/v1/admin/pack-submissions/bulk', { method: 'POST', body });
  if (!res.ok) throw await submissionError(res);
  return parseJson(res, bulkPackSubmissionActionResponseDto);
}

/** POST .../rescan — dispara o scan manualmente (ADR-0731). Re-enfileira + drain; devolve o estado
    (enqueued / runner_unavailable / no_scan_blob). Não é uma transição de status. */
export async function rescanPackSubmission(id: string): Promise<RescanResponse> {
  const res = await request(`/v1/admin/pack-submissions/${encodeURIComponent(id)}/rescan`, {
    method: 'POST',
    body: {},
  });
  if (!res.ok && res.status !== HTTP_CONFLICT) throw await submissionError(res);
  return parseJson(res, rescanResponseDto);
}
